'use client';

import React, { useState } from 'react';
// import { PlayerInfoModal } from './player-info-modal'
import { Player } from '@repo/types';
import { RefreshCw, X } from 'lucide-react';
import Image from 'next/image';
import { toMillionPrice } from '../../lib/utils';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { Clover } from 'lucide-react';
import { PlayerActionModal } from './player-action-modal';

interface DriverCardProps {
  driver?: Player;
  cardIndex?: number | undefined;
  type?: string;
  onRemove?: () => void;
  onSelect?: () => void;
  showPoints?: boolean;
  hasCaptainBoosterEnabled?: any;
  isSelected?: boolean;
  onSwap?: () => void;
  swapLabel?: string;
  compact?: boolean;
}

export function DriverCard({
  driver,
  cardIndex,
  type,
  onRemove,
  onSelect,
  showPoints,
  hasCaptainBoosterEnabled,
  isSelected,
  onSwap,
  swapLabel,
  compact = false
}: DriverCardProps) {
  const [showInfo, setShowInfo] = useState(false);
  const { competitionData } = useCompetitionContext();

  if (!driver) {
    return (
      <div className={compact ? "pt-1 w-[4.1rem] h-[5.25rem]" : "pt-2 w-[6.5rem] h-28 mx-1"}>
        <div className="w-full h-full rounded-lg overflow-hidden border-2 border-dashed border-white/40 bg-white/10 backdrop-blur-sm">
          <div className="h-full flex items-center justify-center">
            <button
              className="w-full h-full focus:outline-none flex items-center justify-center"
              onClick={onSelect}
            >
              <span className={compact ? "font-bold text-white/70 text-lg" : "font-bold text-white/70 text-2xl"}>+</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "w-[4.1rem] h-[5.25rem] pt-1" : "w-[6.5rem] h-28 pt-1 mx-1"}>
      <div className={`relative w-full h-full transition-all duration-150 ${isSelected ? `ring-2 ring-[#FDC300] ring-offset-1 ring-offset-transparent rounded-lg ${compact ? 'scale-[1.02]' : 'scale-105'}` : ''}`}>
        {onSwap && (
          <button
            type="button"
            onClick={onSwap}
            className={`absolute -top-1.5 -left-1.5
                     ${isSelected ? 'bg-[#FDC300] text-[#003d60]' : 'bg-white text-gray-600 shadow-sm'}
                     ${compact ? 'p-0.5' : 'p-1'} rounded-full transition-colors z-10 hover:scale-110`}
            aria-label="Permuter"
            title="Permuter"
          >
            <RefreshCw size={compact ? 11 : 13} />
          </button>
        )}
        {(hasCaptainBoosterEnabled && driver.cap === 1) && (
          <span
            className="absolute -top-1.5 -left-1.5
                     text-green-500 bg-white rounded-full shadow-sm z-10"
            style={{ padding: compact ? '0.125rem' : '0.25rem', marginLeft: onSwap ? (compact ? '0.95rem' : '1.1rem') : undefined }}
          >
            <Clover size={compact ? 11 : 14} />
          </span>
        )}
        <div className="w-full h-full rounded-lg shadow-md overflow-hidden bg-white/90 backdrop-blur-sm">
          {/* Image Container */}
          <div className={compact ? "pt-1 pb-0 flex justify-center items-center" : "pt-1.5 pb-0 flex justify-center items-center"}>
            <button 
              onClick={() => setShowInfo(true)}
              className="w-full m-0 p-0 focus:outline-none flex items-center justify-center"
            >
              <Image
                src={`https://ocnc-fantasy.b-cdn.net/jerseys/${driver.clubId}.png`}
                alt={driver.name}
                width={100}
                height={100}
                className={compact ? "w-10 h-10 object-contain drop-shadow-sm" : "w-14 h-14 object-contain drop-shadow-sm"}
                onError={(e) => {
                  e.currentTarget.src = "https://ocnc-fantasy.b-cdn.net/jerseys/906.png";
                }}
              />
            </button>
          </div>
          
          {/* Info Container */}
          <div className="absolute bottom-0 left-0 right-0">
            {/* Name Container */}
            <div className={`${driver.active === 0 ? 'bg-orange-500' : 'bg-[#003d60]'} text-white ${compact ? 'text-[8px]' : 'text-[11px]'}`}>
              <h3 className={`text-center truncate leading-tight font-semibold ${compact ? 'px-0.5 py-0.5' : 'px-1 py-1'}`}>
                {driver.short}
              </h3>
            </div>
            
            {/* Price/Points Container */}
            <div className={`bg-[#FDC300] text-[#003d60] leading-none rounded-b-lg ${compact ? 'text-[8px]' : 'text-[11px]'}`}>
              <div className={`flex justify-center items-center ${compact ? 'py-0.5' : 'py-1'}`}>
                {showPoints ? (
                  <><span className={`font-extrabold ${compact ? 'px-0.5' : 'px-1'}`}>
                  {
                    driver.stats 
                      ? (
                          hasCaptainBoosterEnabled && driver.cap === 1 && driver.stats.r 
                            ? (
                                <span className="inline-flex items-center gap-0.5"><Clover className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />{driver.stats.p * 2}</span>
                              )
                            : `${driver.stats.p}`
                        ) 
                      : `-`
                  }
                </span><span className="font-medium">
                    {driver.stats && driver.stats.w === 22 ? `(bonus)` : driver.stats ? `(${driver.stats.r}e)` : ``}
                  </span></>
                ) : (
                  <span className="font-extrabold">
                    {cardIndex !== undefined
                      ? `${cardIndex + 1}e`
                      : `${toMillionPrice(driver.value)/1_000_000}M`
                    }
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {onRemove && (
          <button
            id={`d_${driver.id}`}
            onClick={onRemove}
            className="absolute -top-1.5 -right-1.5
                     bg-red-500 text-white rounded-full hover:bg-red-600 
                     shadow-sm transition-colors z-10 hover:scale-110"
            style={{ padding: compact ? '0.125rem' : '0.25rem' }}
          >
            <X size={compact ? 11 : 14} />
          </button>
        )}
      </div>

      <PlayerActionModal
        isOpen={showInfo}
        onClose={() => setShowInfo(false)}
        driver={driver}
        clubName={competitionData?.clubs?.find((club: { id: number; name: string }) => club.id === driver.clubId)?.name}
        onSwap={onSwap}
        swapLabel={swapLabel}
        onRemove={onRemove}
      />
    </div>
  );
}
